<template>
  <div class="inventory-type-content">
    <div class="inventory-type-details-container">
      <div class="inventory-type-details-container-header">
        {{ titleText }}
        <AuditInformation 
          v-if="!loading && inventoryType && inventoryType.id > 0 && inventoryType.createdByUser && inventoryType.createdDate && inventoryType.lastModifiedByUser && inventoryType.lastModifiedDate"
          :created-by-user="inventoryType.createdByUser"
          :created-date="inventoryType.createdDate"
          :last-modified-by-user="inventoryType.lastModifiedByUser"
          :last-modified-date="inventoryType.lastModifiedDate"
        />
      </div>
      <LoadingPulse
        :loading="loading"
        class="loading-spinner"
      />
      <div
        v-if="!loading && inventoryType"
        class="inventory-type-details-container-content"
      >
        <LockingButton
          class="save-button"
          :disabled="!isDirty"
          :locked="saving"
          :text="'Save'"
          @click="onSaveInventoryTypeClicked"
        />
        <TextInput
          v-model="inventoryType.name"
          :show-required="true"
          label="Name"
          class="input"
        />
        <NumericInput
          v-model="inventoryType.displayOrder"
          :show-required="true"
          label="Display Order"
          class="input"
        />
      </div>
    </div>
    <div class="fields-container">
      <div class="fields-container-header">
        <div class="fields-container-header-text">
          Fields
        </div>
        <a 
          v-show="!loading && inventoryType && inventoryType.id > 0"
          class="add-field"
          @click="onAddFieldClicked"
        >
          Create
        </a>
      </div>
      <LoadingPulse
        :loading="loading"
        class="loading-spinner"
      />
      <div
        v-if="!loading && inventoryType"
        class="overflow-wrapper"
      >
        <table>
          <thead>
            <tr>
              <th class="no-sort">
                <div>
                  Name
                </div>
              </th>
              <th class="no-sort">
                <div>
                  Type
                </div>
              </th>
              <th class="no-sort">
                <div>
                  Is Required?
                </div>
              </th>
              <th class="no-sort">
                <div>
                  Display Order
                </div>
              </th>
              <th class="no-sort" />
            </tr>
          </thead>
          <tbody v-if="inventoryType && inventoryType.inventoryTypeFields.length > 0">
            <tr
              v-for="inventoryTypeField in inventoryType.inventoryTypeFields"
              :key="inventoryTypeField.id"
              class="clickable"
              @click="onTableRowClicked(inventoryTypeField)"
            >
              <td>{{ inventoryTypeField.name }}</td>
              <td>{{ typeToDisplayValue(inventoryTypeField.type) }}</td>
              <td>{{ boolToString(inventoryTypeField.isRequired) }}</td>
              <td>{{ inventoryTypeField.displayOrder }}</td>
              <td class="delete">
                <TrashCanSvg @click.stop="onDeleteClicked(inventoryTypeField.id)" />
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td
                v-if="inventoryType.id > 0"
                :colspan="5"
                class="message"
              >
                There are no fields on the inventory type. Please click 'Create' above to add a field.
              </td>
              <td
                v-else
                :colspan="5"
                class="message"
              >
                Fields can be added to the inventory type once it is initally created.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <transition name="slide-up">
      <InventoryTypeFieldModal
        v-if="showInventoryTypeFieldModal && inventoryTypeFieldToEdit"
        :inventory-type-field="inventoryTypeFieldToEdit"
        :inventory-type-id="id"
        @inventory-type-field-changed="getInventoryType"
        @close="showInventoryTypeFieldModal = false"
      />
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import InventoryTypeFieldModal from '@/components/modals/InventoryTypeFieldModal.vue'
import { InventoryTypeDetails } from '@/@types/responses/inventory_type/InventoryTypeDetails'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import { InventoryTypeService } from '@/services/inventoryType.service'
import { InventoryTypeField } from '@/@types/responses/inventory_type/InventoryTypeField'
import { InventoryTypeFieldService } from '@/services/inventoryTypeField.service'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import NumericInput from '@/components/shared/NumericInput.vue'
import { InventoryTypeMessage } from '@/@types/messages/InventoryTypeMessage'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'InventoryType',
  components: {
    AuditInformation,
    InventoryTypeFieldModal,
    LoadingPulse,
    LockingButton,
    NumericInput,
    TextInput,
    TrashCanSvg
  },
  beforeRouteLeave() {
    if (this.showInventoryTypeFieldModal) {
      this.showInventoryTypeFieldModal = false
      return false
    }

    return true
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const inventoryTypeFieldService = inject(PROVIDER_CONSTANTS.inventoryTypeFieldService) as InventoryTypeFieldService
    const inventoryTypeService = inject(PROVIDER_CONSTANTS.inventoryTypeService) as InventoryTypeService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      inventoryTypeFieldService,
      inventoryTypeService,
      toast
    }
  },
  data() {
    let inventoryType: InventoryTypeDetails|undefined
    let inventoryTypeFieldToEdit: InventoryTypeField|undefined
    let unchangedInventoryType: InventoryTypeDetails|undefined

    return {
      inventoryType,
      inventoryTypeFieldToEdit,
      loading: true,
      saving: false,
      showInventoryTypeFieldModal: false,
      unchangedInventoryType
    }
  },
  computed: {
    isDirty(): boolean {
      const a = this.inventoryType
      const b = this.unchangedInventoryType

      return (a?.name !== b?.name || a?.displayOrder !== b?.displayOrder) && (a?.name.trim() !== '' && a?.displayOrder !== undefined && a?.displayOrder >= 0)
    },
    titleText(): string {
      return !this.inventoryType || this.inventoryType.id === 0 ? 'Create Inventory Type' : 'Edit Inventory Type'
    }
  },
  watch: {
    '$route': {
      handler: function(): void {
        this.updatePageTitle()
      }
    }
  },
  mounted() {
    if (this.id > 0) {
      this.getInventoryType()
    } else {
      this.inventoryType = new InventoryTypeDetails(0)
      this.unchangedInventoryType = new InventoryTypeDetails(0)
      this.loading = false
    }
  },
  methods: {
    boolToString(bool: boolean): string {
      return bool ? 'Yes' : 'No'
    },
    getInventoryType(): Promise<void> {
      return this.inventoryTypeService.getInventoryType(this.inventoryType ? this.inventoryType.id : this.id).then(inventoryType => {
        this.inventoryType = inventoryType
        this.unchangedInventoryType = JSON.parse(JSON.stringify(inventoryType))
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        this.saving  = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the inventory type. Try reloading the page.')
        }
      })
    },
    onAddFieldClicked(): void {
      if (!this.inventoryType) return

      let displayOrder = 1
      if (this.inventoryType.inventoryTypeFields.length > 0) {
        const currentMaxValue = this.inventoryType.inventoryTypeFields[this.inventoryType.inventoryTypeFields.length - 1].displayOrder
        displayOrder = currentMaxValue + 1
      }

      this.inventoryTypeFieldToEdit = new InventoryTypeField(0, displayOrder)
      this.showInventoryTypeFieldModal = true
    },
    onDeleteClicked(id: number): void {
      this.inventoryTypeFieldService.deleteInventoryTypeField(id).then(() => {
        this.getInventoryType().then(() => {
          this.toast.success('Field deleted successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the field. Please try again.')
        }
      })
    },
    onSaveInventoryTypeClicked(): void {
      if (!this.inventoryType) return

      this.saving = true
      
      const inventoryTypeMessage = new InventoryTypeMessage(this.inventoryType.displayOrder, this.inventoryType.name)
      if (this.inventoryType.id === 0)
        this.inventoryTypeService.createInventoryType(inventoryTypeMessage).then(objectCreatedResponse => {
          if (!this.inventoryType) return

          this.inventoryType.id = objectCreatedResponse.id
          this.getInventoryType().then(() => {
            this.$router.replace(`/inventory-type/${objectCreatedResponse.id}`)
            this.saving = false
            this.toast.success('The inventory type was created successfully.')
          })
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the inventory type. Please try again.')
          }
        })
      else
        this.inventoryTypeService.updateInventoryType(this.id, inventoryTypeMessage).then(() => {
          this.toast.success('The inventory type was updated successfully.')
          this.saving = false
          this.$router.push('/inventory-types')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the inventory type. Please try again.')
          }
        })
    },
    onTableRowClicked(inventoryTypeField: InventoryTypeField): void {
      this.inventoryTypeFieldToEdit = inventoryTypeField
      this.showInventoryTypeFieldModal = true
    },
    typeToDisplayValue(type: number): string {
      switch(type) {
        case 1: return 'Manufacturer'
        case 2: return 'Supplier'
        case 3: return 'Free-Form Text'
        case 4: return 'Selection'
        case 5: return 'Yes / No'
        case 6: return 'Number'
        case 7: return 'Build Step'
        case 8: return 'Caliber'
        default: return ''
      }
    },
    updatePageTitle(): void {
      if (this.id > 0 && this.inventoryType)
        document.title = `${this.inventoryType.name} - Short Action Customs, LLC`
    }
  }
})
</script>

<style scoped lang="scss">
.inventory-type-content {
  display: flex;
  flex-direction: column;
  padding: 15px;

  @include desktop {
    flex-direction: row;
  }

  > .inventory-type-details-container {
    display: flex;
    flex-direction: column;
    margin-bottom: 15px;
    position: relative;

    @include desktop {
      flex: 1 1 35%;
      height: max-content;
      padding: 15px;
      background-color: $Color-fill-gray;
      border-radius: 2px;
      @include element-shadow;
    }

    > .inventory-type-details-container-header {
      line-height: 37px;
      font-size: 24px;
      font-weight: bold;
      padding-bottom: 10px;
      border-bottom: 1px solid $Color-border-gray;
    }

    > .loading-spinner {
      margin: auto;
      padding: 20px;
    }

    > .inventory-type-details-container-content {
      padding-top: 15px;

      > .save-button {
        position: absolute;
        top: 0;
        right: 0;

        @include desktop {
          top: 15px;
          right: 15px;
        }
      }

      > .input {
        margin-bottom: 15px;

        &:last-of-type {
          margin-top: 15px;
          margin-bottom: 0;
        }
      }
    }
  }

  > .fields-container {
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding-bottom: 15px;

    @include desktop {
      flex: 1 1 65%;
      width: 65%;
      padding-left: 20px;
    }

    > .fields-container-header {
      display: flex;
      margin-bottom: 15px;

      > .fields-container-header-text {
        margin: 0 auto 0 0;
        font-size: 24px;
        font-weight: bold;
      }

      > .add-field {
        @include flat-button;
        margin: auto 0;
      }
    }

    > .loading-spinner {
      margin: auto;
    }

    > .overflow-wrapper {
      overflow-y: auto;
      @include sac-table;
    }
  }

  > .slide-up {
    @include modal-animation;
  }
}
</style>
